import React from 'react';
import {
    ExportOutlined,
    ImportOutlined,
    PlusOutlined
} from '@ant-design/icons';
import { Button, Input, InputNumber, message, Modal, Radio, Table, Divider, notification } from 'antd';
import { Api } from '../../../lib/api';
import { POLARITY_TYPE__A, POLARITY_TYPE__B, POLARITY_TYPE__C } from '@shared/constants/constants';

import '../styles.scss';
import { POLARITY_TYPES } from '../components/pageNameConstants';
import DeleteWarningModal from '../components/DeleteWarningModal';
import { XNSError } from '../../../errors/XNSError';
import { downloadFile, importFile } from '../../../lib/utils';

const regExp = /[a-zA-Z]/g;
const MAX_FIBERS = 999;

interface PolarityTypeState {
    numFibers: number;
    polarityType: string | null;
    polarities: any[];
    name: string;
    mapping: (number | null)[];
    tableIndex: number;
    visible: boolean;
    editVisible: boolean;
    id: string | null;
    showDeleteModal: boolean;
    recordToDelete: any;
    currentRecordId?: number | null;
    currentRecordName?: string | null;
    createNew?: boolean;
    selectedRows?: any[];
    selectedRowKeys?: React.Key[];
}

export default class PolarityTypes extends React.PureComponent<{}, PolarityTypeState> {
    state: PolarityTypeState = {
        numFibers: 12,
        polarityType: null,
        polarities: [],
        name: '',
        mapping: Array(12)
            .fill('')
            .map((v, i) => i + 1),
        tableIndex: 0,
        visible: false,
        editVisible: false,
        id: null,
        showDeleteModal: false,
        recordToDelete: null,
    };

    updatePolarities = async () => {
        await Api.getPolarities().then((polarities) => {
            this.setState({
                polarities,
                selectedRows: this.state.selectedRows?.filter(row => polarities.some(polarity => polarity.id === row.id)) || [],
                selectedRowKeys: this.state.selectedRowKeys?.filter(key => polarities.some(polarity => polarity.name === key)) || []
            });
        });
    }

    deletePolarities = async (record) => {
        try {
            const {status, json: result} = await Api.deletePolarity(record);
            if(!result || status !== 200) {
                throw new Error('Invalid response from server');
            }

            message.success('Polarity type deleted successfully');

            this.updatePolarities();

            return result;
        } catch (error: any) {
            if(error instanceof XNSError) {
                notification.error({
                    placement: 'bottomRight',
                    duration: 0,
                    message: error.title || 'Error deleting polarity type',
                    description: error.message || 'An unexpected error occurred while deleting the polarity type'
                });
            }
        }
    };

    componentDidMount() {
        this.updatePolarities();
    }

    checkMapping() {
        let mapping = [...this.state.mapping];

        // Extend the array
        mapping.length = this.state.numFibers;

        // Fill in the new section with empty strings
        mapping.fill(null, this.state.mapping.length);

        this.setState({ mapping });
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.state.numFibers !== prevState.numFibers && this.state.mapping.length < this.state.numFibers) {
            // Copy array
            let mapping = [...this.state.mapping];

            // Extend the array
            mapping.length = this.state.numFibers;

            // Fill in the new section with empty strings
            mapping.fill(null, this.state.mapping.length);

            this.setState({ mapping });
        }

        if (
            (this.state.createNew !== prevState.createNew ||
                this.state.polarityType !== prevState.polarityType ||
                this.state.numFibers !== prevState.numFibers) &&
            (this.state.polarityType === POLARITY_TYPE__A ||
                this.state.polarityType === POLARITY_TYPE__B ||
                this.state.polarityType === POLARITY_TYPE__C)
        ) {
            if (this.state.createNew) {
                // Do nothing
            } else if (this.state.polarityType === POLARITY_TYPE__A) {
                this.setState((state) => ({
                    mapping: Array(state.numFibers)
                        .fill('')
                        .map((v, i) => i + 1),
                    tableIndex: state.tableIndex + 1
                }));
            } else if (this.state.polarityType === POLARITY_TYPE__B) {
                this.setState((state) => ({
                    mapping: Array(state.numFibers)
                        .fill('')
                        .map((v, i) => state.numFibers - i),
                    tableIndex: state.tableIndex + 1
                }));
            } else if (this.state.polarityType === POLARITY_TYPE__C) {
                this.setState((state) => ({
                    mapping: Array(state.numFibers)
                        .fill('')
                        .map((v, i) => (i % 2 ? i : i + 1 === state.numFibers ? i + 2 : i + 2)),
                    tableIndex: state.tableIndex + 1
                }));
            }
        }

        if (
            this.state.polarityType !== prevState.polarityType &&
            this.state.polarityType !== POLARITY_TYPE__A &&
            this.state.polarityType !== POLARITY_TYPE__B &&
            this.state.polarityType !== POLARITY_TYPE__C
        ) {
            let polarityType = this.state.polarities.find((polarity) => polarity.id === this.state.polarityType);
            if (polarityType) {
                this.setState((state) => ({
                    numFibers: polarityType.fibers,
                    mapping: polarityType.polarity.split(',').map((v) => (v === '-' ? '' : v)),
                    tableIndex: state.tableIndex + 1
                }));
            }
        }
    }
    showModal = () => {
        this.setState({
            numFibers: 12,
            polarityType: null,
            name: '',
            mapping: Array(12)
                .fill('')
                .map((v, i) => i + 1),
            tableIndex: 0,
            visible: true
        });
    };

    handleCancel = (e) => {
        this.setState({
            numFibers: 12,
            polarityType: null,
            name: '',
            visible: false,
            editVisible: false,
            id: null
        });
    };

    closeDeleteModal = () => {
        this.setState({showDeleteModal: false});
    }

    render() {
        let numRows = Math.ceil(this.state.numFibers / 12);

        return (
            <div className="section polarity-types page-card">
                <div className="create-button">
                    <div style={{ display: 'flex', gap: '6px' }}>
                        {
                            this.state.selectedRowKeys && this.state.selectedRowKeys.length > 0 
                                ? (
                                    <>
                                        <Button
                                            shape="round" 
                                            onClick={() => this.setState({selectedRowKeys: [], selectedRows: []})}
                                        >
                                            Clear Selection
                                        </Button>
                                        <Button 
                                            type="primary" 
                                            shape="round" 
                                            icon={<ExportOutlined />}
                                            onClick={() => {
                                                const selectedPolarityIds = this.state.selectedRows?.map(row => row.id) || [];
                                                
                                                const exportData = this.state.polarities
                                                    .filter(polarity => selectedPolarityIds.includes(polarity.id))
                                                    .map(polarity => ({
                                                        name: polarity.name,
                                                        fibers: polarity.fibers,
                                                        polarity: polarity.polarity
                                                    }));
                                                
                                                let fileName = '';
                                                if(exportData.length === 1) {
                                                    fileName = `polarity-${exportData[0].name}.json`;
                                                } else if(exportData.length > 1) {
                                                    fileName = 'selected_polarities.json';
                                                } else {
                                                    message.error('No polarity types selected for export');
                                                    return;
                                                }

                                                downloadFile(fileName, exportData, 'application/json');

                                                this.setState({selectedRowKeys: [], selectedRows: [] });
                                            }}
                                        >
                                            Export Selected ({this.state.selectedRowKeys.length})
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <Button
                                            shape="round"
                                            icon={<ImportOutlined />}
                                            onClick={async () => {
                                                importFile(async (file) => {
                                                    let fileReader = new FileReader();
    
                                                    fileReader.onload = async (event) => {
                                                        try {
                                                            const fileContent = JSON.parse(event.target?.result as string);
    
                                                            const { createdPolarities, skippedPolarities } = await Api.importPolarity({ polarities: fileContent });

                                                            if (createdPolarities.length > 0 && skippedPolarities.length === 0) {
                                                                message.success(`Successfully imported ${createdPolarities.length} polarity(s)`, 5);
                                                            } else if (createdPolarities.length > 0 && skippedPolarities.length > 0) {
                                                                message.success(`Imported ${createdPolarities.length} polarity(s). Skipped ${skippedPolarities.length} (identical entries already exist)`, 5);
                                                            } else if (skippedPolarities.length > 0) {
                                                                message.info(`All ${skippedPolarities.length} polarity(s) already exist with identical mappings. Nothing imported.`, 5);
                                                            }

                                                            this.updatePolarities();
                                                        } catch (error) {
                                                            message.error('Import failed. Please ensure the file is a valid polarity export.', 5);
                                                        }
                                                    };
    
                                                    fileReader.readAsText(file);
                                                });
                                            }}
                                        >
                                            Import
                                        </Button>
                                        <Button type="primary" shape="round" icon={<PlusOutlined />} onClick={this.showModal}>
                                            New Polarity
                                        </Button>
                                    </>
                                )
                        }
                    </div>
                </div>
                {this.state.polarities && (
                    <Table
                        columns={[
                            {
                                title: 'Name',
                                dataIndex: 'name',
                                key: 'name',
                                render: (text, record) => <div>{text}</div>
                            },
                            {
                                title: 'Fibers',
                                dataIndex: 'fibers',
                                key: 'fibers'
                            },
                            {
                                title: 'Action',
                                key: 'action',
                                render: (text, record) => (
                                    <div className="action-buttons">
                                        <Button
                                            type="link"
                                            style={{ color: '#40a9ff' }}
                                            onClick={() => {
                                                this.setState({
                                                    id: record.id,
                                                    numFibers: record.fibers,
                                                    name: record.name,
                                                    mapping: record.polarity.split(',').map((v) => (v === '-' ? '' : +v)),
                                                    editVisible: true
                                                });
                                            }}
                                        >
                                            Edit
                                        </Button>
                                        <Button
                                            type="link"
                                            style={{ color: '#40a9ff' }}
                                            onClick={() => downloadFile(
                                                `polarity-${record.name}.json`,
                                                [{
                                                    name: record.name,
                                                    fibers: record.fibers,
                                                    polarity: record.polarity
                                                }],
                                                'application/json'
                                            )}
                                        >
                                            Export
                                        </Button>
                                        <Divider type="vertical" />
                                        <Button
                                            type="link"
                                            style={{ color: '#e60013' }}
                                            onClick={() => {
                                                this.setState({
                                                    showDeleteModal: true,
                                                    currentRecordId: record.id,
                                                    currentRecordName: record.name,
                                                    recordToDelete: record
                                                });
                                            }}
                                        >
                                            Delete
                                        </Button>
                                    </div>
                                )
                            }
                        ]}
                        rowSelection={{
                            selectedRowKeys: this.state.selectedRowKeys,
                            onChange: (selectedRowKeys, selectedRows) => this.setState({ selectedRowKeys, selectedRows })
                        }}
                        dataSource={this.state.polarities}
                        pagination={{ pageSize: 8 }}
                        rowKey="name"
                    />
                )}
                <DeleteWarningModal
                    isOpen={this.state.showDeleteModal}
                    itemTypeToDelete={POLARITY_TYPES}
                    itemNameToDelete={this.state.currentRecordName || ''}
                    itemRecordToDelete={this.state.currentRecordId!}
                    closeCallback={this.closeDeleteModal}
                    update={this.updatePolarities}
                    delete={this.deletePolarities}
                >
                </DeleteWarningModal>
                {this.state.visible && (
                    <Modal
                        title="Create Polarity"
                        open={this.state.visible}
                        width="820px"
                        onCancel={this.handleCancel}
                        footer={
                            <div className="footer">
                                <div className="buttons">
                                    <Button
                                        type="primary"
                                        onClick={() => {
                                            if (this.state.mapping.includes(null)) {
                                                alert('Please fill all the required fields.');
                                                return;
                                            }
                                            let mapping = this.state.mapping
                                                .slice(0, this.state.numFibers)
                                                .map((v) => (v === null ? '-' : v))
                                                .join(',');

                                            Api.createPolarity(this.state.name, this.state.numFibers, mapping)
                                                .then((polarity) => {
                                                    this.updatePolarities();
                                                    this.setState({
                                                        name: '',
                                                        numFibers: 12,
                                                        polarityType: null,
                                                        visible: false,
                                                        id: null
                                                    });
                                                })
                                                .catch(() => {
                                                    message.error('Failed to create polarity type');
                                                });
                                        }}
                                    >
                                        Create
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            this.setState({
                                                numFibers: 12,
                                                polarityType: null,
                                                name: '',
                                                visible: false,

                                                id: null
                                            });
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        }
                    >
                        <React.Fragment>
                            <div className="title">Polarity Type:</div>

                            <Input
                                defaultValue={this.state.name}
                                onChange={(e) => this.setState({ name: e.target.value })}
                                autoFocus
                            />
                            <div className="title">Preset:</div>
                            <Radio.Group
                                defaultValue={this.state.polarityType}
                                onChange={(value) => {
                                    this.setState({ polarityType: value.target.value });
                                }}
                            >
                                <Radio.Button value={POLARITY_TYPE__A}>A</Radio.Button>
                                <Radio.Button value={POLARITY_TYPE__B}>B</Radio.Button>
                                <Radio.Button value={POLARITY_TYPE__C}>C</Radio.Button>
                            </Radio.Group>
                            <div className="fiber-number">
                                <div className="title">Number of Fibers:</div>
                                <InputNumber
                                    defaultValue={this.state.numFibers}
                                    min={1}
                                    max={MAX_FIBERS}
                                    onChange={(value) => this.setState({ numFibers: value ?? 1 })}
                                />
                            </div>
                            <React.Fragment key={this.state.tableIndex}>
                                <div className="cable-mapping-table-2">
                                    {
                                        // For each row
                                        [...Array(numRows)].map((v1, rowIndex) => (
                                            <React.Fragment key={'row-' + rowIndex}>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        fontStyle: 'italic',
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    <div>
                                                        Output({12 * rowIndex + 1}-{12 * rowIndex + 12})
                                                    </div>
                                                    <div style={{ color: 'blue' }}>
                                                        Input ({12 * rowIndex + 1}-{12 * rowIndex + 12})
                                                    </div>
                                                </div>
                                                {
                                                    // For each column
                                                    [
                                                        ...Array(
                                                            rowIndex + 1 < numRows
                                                                ? 12
                                                                : this.state.numFibers - (numRows - 1) * 12
                                                        )
                                                    ].map((v2, columnIndex) => (
                                                        <div
                                                            key={'cell-' + (rowIndex * 12 + columnIndex)}
                                                            style={{ display: 'flex', flexDirection: 'column' }}
                                                        >
                                                            <div>{12 * rowIndex + 1 + columnIndex}</div>
                                                            <InputNumber
                                                                defaultValue={this.state.mapping[rowIndex * 12 + columnIndex] ?? 1}
                                                                onChange={(value) => {
                                                                    let sanitizedValue = value ?? Number.NaN;

                                                                    if (sanitizedValue >= 0 && sanitizedValue < 10000) {
                                                                        this.setState((state) => {
                                                                            let mapping = [...state.mapping];
                                                                            mapping[rowIndex * 12 + columnIndex] = sanitizedValue;
                                                                            return { mapping };
                                                                        });
                                                                    }
                                                                }}
                                                                min={0}
                                                                style={{ color: 'blue' }}
                                                            />
                                                        </div>
                                                    ))
                                                }
                                            </React.Fragment>
                                        ))
                                    }
                                </div>
                            </React.Fragment>
                        </React.Fragment>
                    </Modal>
                )}

                {this.state.id && (
                    <Modal
                        title="Edit Polarity"
                        open={this.state.editVisible}
                        width="780px"
                        onCancel={this.handleCancel}
                        footer={
                            <div className="footer">
                                <div className="buttons">
                                    <Button
                                        type="primary"
                                        onClick={() => {
                                            let mapping = this.state.mapping
                                                .slice(0, this.state.numFibers)
                                                .map((v) => (v === null ? '-' : v))
                                                .join(',');
                                            Api.updatePolarity(this.state.id, {
                                                name: this.state.name,
                                                polarity: mapping,
                                                fibers: this.state.numFibers
                                            })
                                                .then(() => {
                                                    message.success('Save successful', 3);
                                                    this.updatePolarities();
                                                    this.setState({
                                                        numFibers: 12,
                                                        polarityType: null,
                                                        name: '',
                                                        editVisible: false,
                                                        id: null
                                                    });
                                                })
                                                .catch(() => {
                                                    message.error('Save failed');
                                                });
                                        }}
                                    >
                                        Save
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            this.setState({
                                                numFibers: 12,
                                                polarityType: null,
                                                name: '',

                                                editVisible: false,
                                                id: null
                                            });
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        }
                    >
                        <React.Fragment>
                            <div className="title">Polarity Type:</div>
                            <Input
                                defaultValue={this.state.name}
                                onChange={(e) => this.setState({ name: e.target.value })}
                                autoFocus
                            />
                            <div className="title">Preset:</div>
                            <Radio.Group
                                defaultValue={this.state.polarityType}
                                onChange={(value) => {
                                    this.setState({ polarityType: value.target.value });
                                }}
                            >
                                <Radio.Button value={POLARITY_TYPE__A}>A</Radio.Button>
                                <Radio.Button value={POLARITY_TYPE__B}>B</Radio.Button>
                                <Radio.Button value={POLARITY_TYPE__C}>C</Radio.Button>
                            </Radio.Group>
                            <div className="title">
                                <div>Number of Fibers:</div>
                                <InputNumber
                                    defaultValue={this.state.numFibers}
                                    min={1}
                                    max={MAX_FIBERS}
                                    onChange={(value) => this.setState({ numFibers: value ?? 1 })}
                                />
                            </div>
                            <React.Fragment key={this.state.tableIndex}>
                                <div className="cable-mapping-table-2">
                                    <div className="title">Output</div>
                                    {
                                        // Add 12 empty cells
                                        [...Array(12)].map((value, index) => (
                                            <div key={'header-' + index} style={{ fontWeight: '600', marginLeft: '17px' }}>
                                                {index > this.state.numFibers - 1 ? <>&nbsp;</> : index + 1}
                                            </div>
                                        ))
                                    }

                                    {
                                        // For each row
                                        [...Array(numRows)].map((v1, rowIndex) => (
                                            <React.Fragment key={'row-' + rowIndex}>
                                                <div>
                                                    Input ({12 * rowIndex + 1}-{12 * rowIndex + 12})
                                                </div>
                                                {
                                                    // For each column
                                                    [
                                                        ...Array(
                                                            rowIndex + 1 < numRows
                                                                ? 12
                                                                : this.state.numFibers - (numRows - 1) * 12
                                                        )
                                                    ].map((v2, columnIndex) => (
                                                        <div key={'cell-' + (rowIndex * 12 + columnIndex)}>
                                                            <InputNumber
                                                                defaultValue={this.state.mapping[rowIndex * 12 + columnIndex] ?? 1}
                                                                onChange={(value) => {
                                                                    let sanitizedValue = value ?? Number.NaN;

                                                                    if (sanitizedValue >= 0 && sanitizedValue < 10000) {
                                                                        this.setState((state) => {
                                                                            let mapping = [...state.mapping];
                                                                            mapping[rowIndex * 12 + columnIndex] = sanitizedValue;
                                                                            return { mapping };
                                                                        });
                                                                    }
                                                                }}
                                                                min={0}
                                                                style={{ color: 'blue' }}
                                                            />
                                                        </div>
                                                    ))
                                                }
                                            </React.Fragment>
                                        ))
                                    }
                                </div>
                            </React.Fragment>
                        </React.Fragment>
                    </Modal>
                )}
            </div>
        );
    }
}